/**
 * The Trash (W2-10 projects-data; DESIGN.md §4.1, §5.16), route `#/trash`: charts moved to the Trash stay 30 days,
 * then persistence deletes them. Each can be restored or deleted for good (asks first); "Empty Trash" deletes them
 * all (asks first). Rendered inside the Library page, which keeps the list current (also across tabs).
 *
 * Keyboard: when a chart leaves the Trash from its own button, focus goes to the same button of the next chart (the
 * previous one at the end), or to "Back to Library" when the Trash is empty.
 */

import { useCallback, useMemo, useRef } from 'react';
import type { AppServices } from '../../app/registry/types';
import type { ProjectSummary } from '../../persist/types';
import { Button, EmptyState, Icon } from '../../ui';
import { refocusIfLost, useFocusHandoff } from './focusHandoff';
import { useNow, useText, type LibraryText } from './hooks';
import { m } from './library.messages';
import { deleteForever, restoreProject } from './libraryActions';
import { formatBytes, relativeTime, trashDaysLeft } from './libraryModel';
import { trashedProjects, useLibraryState, type LibraryUiState } from './libraryStore';
import { levelOf, Thumbnail, titleOf } from './ProjectCard';
import s from './Library.module.css';

const selectProjects = (st: LibraryUiState): LibraryUiState['projects'] => st.projects;
const selectBusy = (st: LibraryUiState): LibraryUiState['busy'] => st.busy;
const selectListedAt = (st: LibraryUiState): number => st.listedAt;

function TrashRow(props: {
  readonly services: AppServices;
  readonly text: LibraryText;
  readonly p: ProjectSummary;
  readonly now: number;
  readonly listedAt: number;
  readonly busy: boolean;
}) {
  const { services, text, p, now } = props;
  const trashedAt = p.trashedAt ?? now;
  const days = trashDaysLeft(trashedAt, Math.max(now, props.listedAt));
  const when = relativeTime(trashedAt, now, text.locale) ?? text.t(m.justNow);
  const busy = props.busy ? { key: m.busy } : null;
  return (
    <li className={s.trashRow} data-project-id={p.id}>
      <Thumbnail blob={p.thumbnail} alt="" />
      <span className={s.trashText}>
        <span className={s.cardTitle}>{titleOf(text, p)}</span>
        <span className={s.cardSubtitle}>{levelOf(text, p)}</span>
        <span className={s.trashMeta}>
          <span>{text.t(m.trashedAt, { when })}</span>
          <span className={s.flag} data-tone={days <= 3 ? 'warning' : 'muted'}>
            <Icon name="Clock" size={14} />
            <span>{text.t(m.trashDaysLeft, { days })}</span>
          </span>
          <span>{formatBytes(p.bytes, text)}</span>
        </span>
      </span>
      <span className={s.trashActions}>
        <Button
          size="sm"
          icon="ArchiveRestore"
          data-focus-control="restore"
          disabledReason={busy}
          onClick={() => {
            void restoreProject(services, p);
          }}
        >
          {text.t(m.restore)}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          icon="Trash"
          data-focus-control="delete"
          disabledReason={busy}
          onClick={() => {
            void deleteForever(services, [p]);
          }}
        >
          {text.t(m.deleteForever)}
        </Button>
      </span>
    </li>
  );
}

export interface TrashViewProps {
  readonly services: AppServices;
}

export function TrashView(props: TrashViewProps) {
  const { services } = props;
  const text = useText(services);
  const now = useNow();
  const projects = useLibraryState(services, selectProjects);
  const busy = useLibraryState(services, selectBusy);
  const listedAt = useLibraryState(services, selectListedAt);
  const trash = useMemo(() => trashedProjects(projects), [projects]);
  const ids = useMemo(() => trash.map((p) => p.id), [trash]);
  const root = useRef<HTMLElement>(null);
  const back = useCallback(
    (): HTMLElement | null => root.current?.querySelector<HTMLElement>('[data-focus-control="back"]') ?? null,
    [],
  );
  const control = useCallback((id: string, name: string | null): HTMLElement | null => {
    const row = [...(root.current?.querySelectorAll<HTMLElement>('li[data-project-id]') ?? [])].find(
      (el) => el.getAttribute('data-project-id') === id,
    );
    return (
      row?.querySelector<HTMLElement>(`[data-focus-control="${name === 'delete' ? 'delete' : 'restore'}"]`) ?? null
    );
  }, []);
  const handoff = useFocusHandoff({ ids, target: control, empty: back });

  return (
    <section ref={root} className={s.trash} aria-labelledby="library-trash-title">
      <header className={s.trashHeader}>
        <Button
          variant="ghost"
          icon="ArrowLeft"
          data-focus-control="back"
          onClick={() => {
            services.nav.goLibrary();
          }}
        >
          {text.t(m.trashBack)}
        </Button>
        <h2 id="library-trash-title" className={s.h2}>
          {text.t(m.trashTitle)}
        </h2>
        {trash.length > 0 && (
          <Button
            variant="secondary"
            className={s.dangerOutline}
            icon="Trash"
            onClick={() => {
              // The button leaves with the last chart: the keyboard goes back to "Back to Library".
              void deleteForever(services, trash).then(() => {
                refocusIfLost(back);
              });
            }}
          >
            {text.t(m.emptyTrash)}
          </Button>
        )}
      </header>
      <p className={s.muted}>{text.t(m.trashIntro)}</p>
      {projects === null ? (
        <p className={s.muted} role="status">
          {text.t(m.loading)}
        </p>
      ) : trash.length === 0 ? (
        <EmptyState icon="Trash2" title={{ key: m.trashEmptyTitle }} body={{ key: m.trashEmptyBody }} />
      ) : (
        <ul
          className={s.trashList}
          aria-label={text.t(m.trashListLabel)}
          onFocus={handoff.onFocus}
          onBlur={handoff.onBlur}
        >
          {trash.map((p) => (
            <TrashRow
              key={p.id}
              services={services}
              text={text}
              p={p}
              now={now}
              listedAt={listedAt}
              busy={busy.has(p.id)}
            />
          ))}
        </ul>
      )}
    </section>
  );
}
